import { api } from "../../services/api";
import {
  loadMessagesFromIndexedDB,
  saveMessageToIndexedDB,
} from "./indexedDB";

type MessagesByChat = Record<string, any[]>;
type MirrorRestoreResponse = {
  messagesByChat?: MessagesByChat;
  pagination?: {
    nextCursor?: string | null;
  };
};

const MAX_CHATS_PER_SYNC = 300;
const MAX_MESSAGES_PER_CHAT_SYNC = 500;
const SYNC_THROTTLE_MS = 15000;
const SYNC_TIMEOUT_MS = 12000;
const RESTORE_PAGE_LIMIT = 700;
const RESTORE_MAX_PAGES = 25;

const lastSyncByUser = new Map<string, number>();
const scheduledSyncByUser = new Map<string, number>();

const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const isMessageMirrorFeatureEnabled = (): boolean => {
  const rawFlag = String(process.env.REACT_APP_ENABLE_MESSAGE_MIRROR || "").trim().toLowerCase();
  if (!rawFlag) return true;
  return !["false", "0", "no", "off", "disabled"].includes(rawFlag);
};

const normalizeTimestamp = (value: any): number => {
  const ts = new Date(value ?? Date.now()).getTime();
  return Number.isNaN(ts) ? Date.now() : ts;
};

const compactMessagesForSync = (
  messagesByChat: MessagesByChat,
): MessagesByChat => {
  const sortedChats = Object.entries(messagesByChat || {})
    .map(([chatId, messages]) => ({
      chatId,
      messages: Array.isArray(messages) ? messages : [],
      lastTs: Array.isArray(messages) && messages.length
        ? normalizeTimestamp(messages[messages.length - 1]?.timestamp)
        : 0,
    }))
    .sort((a, b) => b.lastTs - a.lastTs)
    .slice(0, MAX_CHATS_PER_SYNC);

  const compacted: MessagesByChat = {};
  for (const chat of sortedChats) {
    const dedupedById = new Map<string, any>();
    for (const message of chat.messages) {
      if (!message?.id) continue;
      dedupedById.set(String(message.id), message);
    }

    const compactMessages = [...dedupedById.values()]
      .sort(
        (a, b) => normalizeTimestamp(a?.timestamp) - normalizeTimestamp(b?.timestamp),
      )
      .slice(-MAX_MESSAGES_PER_CHAT_SYNC);

    if (compactMessages.length > 0) {
      compacted[chat.chatId] = compactMessages;
    }
  }

  return compacted;
};

const mergeMessagesByChat = (
  base: MessagesByChat,
  incoming: MessagesByChat,
): MessagesByChat => {
  const result: MessagesByChat = { ...base };
  for (const [chatId, messages] of Object.entries(incoming || {})) {
    const existing = result[chatId] || [];
    const byId = new Map<string, any>();

    for (const message of existing) {
      if (message?.id) byId.set(String(message.id), message);
    }

    for (const message of messages || []) {
      if (!message?.id) continue;
      byId.set(String(message.id), message);
    }

    result[chatId] = [...byId.values()].sort(
      (a, b) =>
        normalizeTimestamp(a?.timestamp) - normalizeTimestamp(b?.timestamp),
    );
  }
  return result;
};

export const syncDesktopMirrorNow = async ({
  userId,
  trigger,
  force = false,
}: {
  userId: string;
  trigger: string;
  force?: boolean;
}) => {
  if (!isMessageMirrorFeatureEnabled()) {
    return { synced: false, reason: "feature-disabled" };
  }

  if (!userId) return { synced: false, reason: "missing-user" };

  const now = Date.now();
  const lastSyncAt = lastSyncByUser.get(userId) || 0;
  if (!force && now - lastSyncAt < SYNC_THROTTLE_MS) {
    return { synced: false, reason: "throttled" };
  }

  const localMessages = await loadMessagesFromIndexedDB(userId);
  const compacted = compactMessagesForSync(localMessages);
  if (Object.keys(compacted).length === 0) {
    return { synced: false, reason: "empty-local-cache" };
  }

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await api.post(
        "/messages/mirror/sync",
        {
          userId,
          source: `frontend:${trigger}`,
          messagesByChat: compacted,
        },
        { timeout: SYNC_TIMEOUT_MS },
      );

      lastSyncByUser.set(userId, Date.now());
      return {
        synced: true,
        chats: Object.keys(compacted).length,
      };
    } catch (error) {
      if (attempt === 3) throw error;
      const jitter = Math.floor(Math.random() * 400);
      await sleep(500 * attempt + jitter);
    }
  }

  return { synced: false, reason: "failed-after-retry" };
};

export const scheduleDesktopMirrorSync = ({
  userId,
  trigger,
}: {
  userId: string;
  trigger: string;
}) => {
  if (!isMessageMirrorFeatureEnabled()) return;
  if (!userId) return;

  if (scheduledSyncByUser.has(userId)) {
    clearTimeout(scheduledSyncByUser.get(userId));
  }

  const timeoutId = window.setTimeout(() => {
    scheduledSyncByUser.delete(userId);
    void syncDesktopMirrorNow({ userId, trigger }).catch((error) => {
      console.warn("[MIRROR] Falha no sync agendado:", error);
    });
  }, 2500);

  scheduledSyncByUser.set(userId, timeoutId);
};

export const restoreMessagesFromMirrorIfNeeded = async ({
  userId,
  force = false,
}: {
  userId: string;
  force?: boolean;
}) => {
  if (!isMessageMirrorFeatureEnabled()) {
    return { restored: false, reason: "feature-disabled" };
  }

  if (!userId) {
    return { restored: false, reason: "missing-user" };
  }

  const localMessages = await loadMessagesFromIndexedDB(userId);
  const hasLocalMessages = Object.values(localMessages).some(
    (messages) => Array.isArray(messages) && messages.length > 0,
  );

  if (hasLocalMessages && !force) {
    return { restored: false, reason: "already-has-local-data" };
  }

  let cursor: string | null = null;
  let pages = 0;
  let merged: MessagesByChat = {};
  let totalFetched = 0;

  while (pages < RESTORE_MAX_PAGES) {
    const response: { data: MirrorRestoreResponse } = await api.get(
      "/messages/mirror/restore",
      {
        params: {
          userId,
          limit: RESTORE_PAGE_LIMIT,
          cursor: cursor || undefined,
        },
        timeout: SYNC_TIMEOUT_MS,
      },
    );

    const payload: MirrorRestoreResponse = response.data;
    const messagesByChat = (payload?.messagesByChat || {}) as MessagesByChat;
    merged = mergeMessagesByChat(merged, messagesByChat);

    totalFetched += Object.values(messagesByChat).reduce(
      (acc, chatMessages) =>
        acc + (Array.isArray(chatMessages) ? chatMessages.length : 0),
      0,
    );

    cursor = payload?.pagination?.nextCursor || null;
    pages += 1;

    if (!cursor) break;
  }

  const hasMirrorData = Object.keys(merged).length > 0;
  if (!hasMirrorData) {
    return { restored: false, reason: "mirror-empty", fetched: totalFetched };
  }

  await saveMessageToIndexedDB({
    userId,
    messages: merged,
  });

  return {
    restored: true,
    chats: Object.keys(merged).length,
    fetched: totalFetched,
    pages,
  };
};
